import { Injectable } from '@angular/core';
import { uniq } from '@sama/micro-dash';
import { mapValues, omit } from 'lodash';
import { ObjectState } from '../../../../cross-team-shared/annotation/shared/state/object-state';
import { TaskWorkspaceState } from '../../../h3ml/state/task-workspace-state';
import { getDiff } from '../../../production-workspace/submissions/get-diff';
import { getDynamicTagDiff } from '../../../production-workspace/submissions/persistence-manager-helper';
import { InternalToExternalConverter } from '../../../shared/internal-external-converters/internal-to-external-object-converter';
import { AbstractChangeOperationService } from './abstract-change-operation.service';
import { createDeleteObjectLocationBuilders } from './operation-builders/delete-object-location-operation-builder';
import { createDeleteObjectOperationBuilders } from './operation-builders/delete-object-operation-builder';
import {
  ObjectLocationOperationBuilderParams,
  ObjectLocationParams,
} from './operation-builders/object-operation-builder-params';
import { createUpsertObjectLocationBuilders } from './operation-builders/upsert-object-location-operation-builder';
import { UpsertObjectOperationBuilder } from './operation-builders/upsert-object-operation-builder';

@Injectable()
export class ObjectChangeOperationService extends AbstractChangeOperationService<ObjectLocationOperationBuilderParams> {
  protected override getEntries(
    previous: TaskWorkspaceState,
    current: TaskWorkspaceState,
  ) {
    const objectDiff = this.getObjectDiff(previous, current);

    return objectDiff.map((objectEntry) => {
      const objectId = Number(objectEntry.objectId);
      const currentObject = current.objects[objectId];
      const previousObject = previous.objects[objectId];
      const builderParams: ObjectLocationOperationBuilderParams = {
        currentObject,
        previousObject,
        locations: this.getChangedLocations(previousObject, currentObject),
      };

      return builderParams;
    });
  }

  protected override createBuilders(
    entry: ObjectLocationOperationBuilderParams,
  ) {
    return [
      new UpsertObjectOperationBuilder(entry),
      ...createDeleteObjectOperationBuilders(entry),
      ...createUpsertObjectLocationBuilders(entry),
      ...createDeleteObjectLocationBuilders(entry),
    ];
  }

  private getObjectDiff(
    previous: TaskWorkspaceState,
    current: TaskWorkspaceState,
  ) {
    return getDiff(previous, current, {
      selectors: [
        {
          getNextLevel: (state) =>
            mapValues(state.objects, (object) =>
              omit(object, ['childObjectIds', 'childShapeIds']),
            ),
          keyedBy: 'objectId',
        },
      ],
      equalityDepthAtLastLevel: 1,
    });
  }

  private getChangedLocations(
    previousObject?: ObjectState,
    currentObject?: ObjectState,
  ) {
    if (!currentObject) {
      return [];
    }
    const changedFrameNumbers = uniq(
      getDynamicTagDiff(previousObject, currentObject).map((entry) =>
        Number(entry.frameNumber),
      ),
    );
    const converter = new InternalToExternalConverter();
    const locations = changedFrameNumbers.map((frameNumber) => {
      const params: ObjectLocationParams = {
        frameNumber,
        operationAttributes: converter.convertDynamicTagsToExternal(
          currentObject,
          frameNumber,
        ),
      };
      return params;
    });

    return locations;
  }
}
